import { useState } from 'react';
import { Box, Button, Card, Grid, Skeleton, Stack, Typography } from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import PhoneAndroidRounded from '@mui/icons-material/PhoneAndroidRounded';
import type { OfferedPlan } from '@/api/types';
import { IntervalToggle, PlanPicker } from '@/billing/PlanPicker';
import { money, type Interval } from '@/billing/planCopy';
import { usePublicPlans } from '../api';

const EXTRA_ICON: Record<string, typeof AutoAwesomeRounded> = {
  'ia-extra': AutoAwesomeRounded,
  'dominio-proprio': LanguageRounded,
  'app-marca-propria': PhoneAndroidRounded,
};

/** Real plans and prices (from the catalog), plus the free marketplace profile and extras. */
export function PlanCards({
  onInterest,
  marketplace,
}: {
  onInterest: () => void;
  marketplace: string | null;
}) {
  const plans = usePublicPlans();
  const [interval, setInterval] = useState<Interval>('month');
  const start = marketplace ? `${marketplace}/painel?aba=espaco` : null;

  if (plans.isPending) {
    return (
      <Grid container spacing={2.5}>
        {[0, 1, 2].map((i) => (
          <Grid key={i} size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rounded" height={420} />
          </Grid>
        ))}
      </Grid>
    );
  }
  const data = plans.data ?? [];
  const extras = data.filter((p) => p.kind === 'addon');
  const action = (plan: OfferedPlan) => {
    const free = !plan.prices.month && !plan.prices.year;
    if (start) {
      return (
        <Button fullWidth variant={plan.key === 'pro' ? 'contained' : 'outlined'} href={start}>
          {free ? 'Criar meu espaço grátis' : `Começar e testar ${plan.trialDays || 14} dias`}
        </Button>
      );
    }
    return (
      <Button fullWidth variant="outlined" onClick={onInterest}>
        Quero conhecer
      </Button>
    );
  };

  return (
    <Stack spacing={4}>
      <Stack sx={{ alignItems: 'center' }}>
        <IntervalToggle value={interval} onChange={setInterval} />
      </Stack>
      {data.length > 0 ? (
        <PlanPicker plans={data} interval={interval} action={action} />
      ) : (
        <Button variant="contained" onClick={onInterest} sx={{ alignSelf: 'center' }}>
          Quero conhecer os planos
        </Button>
      )}
      <Grid container spacing={2.5}>
        {marketplace && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Extra
              icon={StorefrontRounded}
              title="Perfil no marketplace"
              price="Grátis"
              text="Apareça para quem procura personal na sua cidade e receba pedidos de alunos."
              action={
                <Button size="small" href={`${marketplace}/painel`}>
                  Criar perfil
                </Button>
              }
            />
          </Grid>
        )}
        {extras.map((extra) => {
          const Icon = EXTRA_ICON[extra.key] ?? AutoAwesomeRounded;
          return (
            <Grid key={extra.key} size={{ xs: 12, sm: 6, md: 3 }}>
              <Extra
                icon={Icon}
                title={extra.name}
                price={extra.prices.month ? `+ ${money(extra.prices.month)}/mês` : 'Sob consulta'}
                text={extra.description ?? ''}
                action={
                  extra.key === 'dominio-proprio' ? (
                    <Button size="small" href="#dominio">
                      Escolher meu domínio
                    </Button>
                  ) : extra.key === 'app-marca-propria' ? (
                    <Button size="small" href="#app-proprio">
                      Ver como fica
                    </Button>
                  ) : undefined
                }
              />
            </Grid>
          );
        })}
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Pagamento por Pix, boleto ou cartão. Sem fidelidade: muda de plano ou volta para o gratuito
        quando quiser. A IA está em fase de demonstração.
      </Typography>
    </Stack>
  );
}

function Extra({
  icon: Icon,
  title,
  price,
  text,
  action,
}: {
  icon: typeof AutoAwesomeRounded;
  title: string;
  price: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <Card sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Box
          sx={(theme) => ({
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            bgcolor: theme.vars.palette.primary.container,
            color: theme.vars.palette.primary.onContainer,
          })}
        >
          <Icon />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
            {price}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, flex: 1 }}>
        {text}
      </Typography>
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Card>
  );
}
